import { useDeploymentMenuAccess } from '@/hooks/useDeploymentMenuAccess';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, ChevronDown } from 'lucide-react';
import { ThemePicker } from '@evi/ui';
import { useAuthStore } from '@/store/auth';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import {
  NAV_SECTIONS,
  BUILDING_STAFF_NAV,
  type NavSection,
  type NavSectionId,
} from '@/config/nav';
import { filterNavSectionsForPersona } from '@/config/visibilityFilter';
import { resolvePersonaId } from '@evi/shared';
import { UserChipMenu } from './UserChipMenu';
import { QuickJump } from './QuickJump';
import { EvieLauncherButton } from '@/components/evie/EvieLauncherButton';
import { useTodayInbox } from '@/hooks/useTodayInbox';
import { todayBadge } from '@/lib/todayInbox';
import { brandHomePath } from '@/lib/brandHome';
import { activeMenuLocation, groupHoldsPage, matchesMenuHref, menuGroupKey } from '@/lib/menuMemory';
import { useMenuGroups } from '@/hooks/useMenuGroups';
import styles from './TopNav.module.css';

interface TopNavProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  onOpenMobileMenu: () => void;
}

/**
 * Unified horizontal top nav. Replaces the icon-rail + flyout shell
 * and the parallel mobile drawer NAV. Single source of truth for
 * nav data is `config/nav.ts` (NAV_SECTIONS).
 *
 * Behavior:
 *   • At ≥1024px (the admin's "below desktop" threshold from
 *     lib/breakpoints.ts) the section pills render inline; clicking a
 *     pill opens its dropdown panel anchored under the pill. The pills
 *     are whatever filterNavSectionsForPersona returns — it drops any
 *     section that empties out, so the count is per persona (a
 *     super_admin has an extra EVI pill that nobody else does). Do not
 *     re-introduce a fixed count here; it was "four" and went stale.
 *   • At <1024px the pills hide and the left-edge hamburger renders;
 *     tapping it opens MobileNavOverlay (slide-in from the right).
 *
 * The 'recents' section is intentionally not surfaced as a pill —
 * it was an icon-rail-era affordance; keeping it would crowd the
 * top row. Localstorage recents still populate (the RecentsFlyout
 * component file is orphaned but harmless).
 */
export function TopNav({ currentPath, onNavigate, onOpenMobileMenu }: TopNavProps) {
  const user = useAuthStore((s) => s.user);
  const assignedDeployment = useDeploymentMenuAccess();

  // Persona-aware visibility filter. Replaces the four prior inline
  // checks (fleetAdminVisible / salesAgentVisible / salesAgentOnly /
  // requiresSuperAdmin) with a single shared helper. See
  // apps/admin/src/config/visibilityFilter.ts for the full semantics.
  // Behaviour preserved bit-for-bit: limited personas (fleet_admin,
  // sales_agent) see only items explicitly listed in their visibleFor;
  // default-tier admins see everything except items whose visibleFor
  // excludes them (e.g. super_admin-only items). Source of truth for
  // SP discrimination remains PublicUser.is_sales_agent (Phase 7g.4a),
  // which resolvePersonaId folds into the persona axis.
  const visibleSections = useMemo<NavSection[]>(() => {
    // Building staff: their one page (config/nav.ts BUILDING_STAFF_NAV).
    if (user?.facilities_only) return BUILDING_STAFF_NAV;
    const persona = resolvePersonaId({
      role: user?.role,
      // template FIRST: resolvePersonaId prefers it, and without it a
      // Site persona resolves to the session tier 'third_party_admin'
      // and falls through to the capability branch — an operator-shaped
      // menu over one building. See PERSONA_MENU in visibilityFilter.ts.
      template: user?.template ?? null,
      is_sales_agent: user?.is_sales_agent ?? false,
    });
    return filterNavSectionsForPersona(NAV_SECTIONS, persona, user?.permissions ?? [], assignedDeployment);
  }, [user?.role, user?.template, user?.is_sales_agent, user?.permissions, user?.facilities_only, assignedDeployment]);

  // The Super Admin's menu opens with Today, a single leaf that navigates straight there, carrying the total waiting
  // (Warren, 21 October 2026, item M). No other menu has a Today section, so nothing below changes for anyone else.
  const superMenu = visibleSections[0]?.id === 'today';
  const todayCount = todayBadge(useTodayInbox(superMenu).data?.total);

  const [openSectionId, setOpenSectionId] = useState<NavSectionId | null>(null);
  // Which groups (NavParent) are open, shared with the phone drawer (hooks/useMenuGroups.ts; Warren, 28 October
  // 2026): a click opens or closes one group and leaves the others, remembered for this user; the group holding the
  // page shown is open while you are on it; a group starts closed.
  const groups = useMenuGroups(user?._id, currentPath);
  // The query too: the EVI shortcuts' Network Chargers is /chargers?scope=network.
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const barRef = useRef<HTMLElement | null>(null);
  const pillRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on route change.
  useEffect(() => { setOpenSectionId(null); }, [currentPath]);

  // ESC closes the dropdown.
  useEffect(() => {
    if (!openSectionId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenSectionId(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openSectionId]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!openSectionId) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (barRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpenSectionId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openSectionId]);

  // Active-section detection: which section's items contain the page
  // shown? Drives the filled-pill highlight. A page the EVI shortcuts
  // repeat lights the Super Admin's own section, not EVI.
  const activeSectionId = useMemo<NavSectionId | null>(
    () => activeMenuLocation(visibleSections, currentPath, search)?.sectionId ?? null,
    [currentPath, search, visibleSections],
  );

  const togglePill = useCallback((id: NavSectionId) => {
    setOpenSectionId((prev) => (prev === id ? null : id));
  }, []);

  // Compute panel left-px anchored to the open pill. position:fixed
  // panel reads --tn-panel-left from inline style.
  const panelLeft = useMemo<number | null>(() => {
    if (!openSectionId) return null;
    const el = pillRefs.current[openSectionId];
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return Math.round(rect.left);
  }, [openSectionId]);

  const openSection = openSectionId
    ? visibleSections.find((s) => s.id === openSectionId) ?? null
    : null;

  const handleNavigate = (href: string) => {
    onNavigate(href);
    setOpenSectionId(null);
  };

  return (
    <>
      <header ref={barRef} className={superMenu ? `${styles.bar} ${styles.barMany}` : styles.bar} role="banner">
        {/* Hamburger — mobile/tablet only (CSS-hidden at ≥1024px). */}
        <button
          type="button"
          className={`${styles.iconBtn} ${styles.hamburger}`}
          onClick={onOpenMobileMenu}
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>

        {/* Brand: the dashboard map for everyone, never Today (lib/brandHome; Warren, 23 October 2026). */}
        <button
          type="button"
          className={styles.brand}
          onClick={() => onNavigate(brandHomePath(user?.role))}
          aria-label="Go to dashboard"
        >
          <img
            src="/evi-logo-mark.png"
            width={28}
            height={28}
            alt=""
            className={styles.brandLogo}
          />
          <span className={styles.brandWord}>EV INITIATIVE</span>
        </button>

        {/* Section pills */}
        <nav className={superMenu ? `${styles.pills} ${styles.pillsMany}` : styles.pills} aria-label="Main navigation">
          {visibleSections.map((section) => {
            const Icon = section.icon;
            const isOpen = openSectionId === section.id;
            const isActive = activeSectionId === section.id;
            const today = section.id === 'today' ? section.items[0] : undefined;
            if (today?.kind === 'leaf') {
              return (
                <button
                  key={section.id}
                  type="button"
                  className={`${styles.pill} ${isActive ? styles.pillActive : ''}`}
                  onClick={() => handleNavigate(today.href)}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={todayCount ? `${section.label}, ${todayCount} waiting` : section.label}
                >
                  <span className={styles.pillIcon}><Icon size={14} /></span>
                  <span>{section.label}</span>
                  {todayCount && <span className={styles.pillBadge} aria-hidden="true">{todayCount}</span>}
                </button>
              );
            }
            return (
              <button
                key={section.id}
                ref={(el) => { pillRefs.current[section.id] = el; }}
                type="button"
                className={`${styles.pill} ${isOpen ? styles.pillOpen : ''} ${isActive ? styles.pillActive : ''}`}
                onClick={() => togglePill(section.id)}
                aria-expanded={isOpen}
                aria-haspopup="menu"
              >
                <span className={styles.pillIcon}><Icon size={14} /></span>
                <span>{section.label}</span>
                <ChevronDown size={12} className={styles.pillChev} />
              </button>
            );
          })}
        </nav>

        {/* Right rail — EVII sits LEFT of the theme selector (Warren). */}
        <div className={styles.right}>
          <EvieLauncherButton />
          <ThemePicker />
          <NotificationBell buttonClassName={styles.iconBtn} />
          <UserChipMenu onNavigate={onNavigate} />
        </div>
      </header>

      {/* Ctrl+K quick jump over this same menu (Warren, 21 October 2026, item M); it renders nothing for a short menu. */}
      <QuickJump sections={visibleSections} onNavigate={handleNavigate} />

      {/* Dropdown panel — rendered outside <header> so it stacks above
          .content without being clipped by the bar's overflow. */}
      {openSection && panelLeft !== null && (
        <div
          ref={panelRef}
          className={styles.panel}
          style={{ ['--tn-panel-left' as string]: `${panelLeft}px` }}
          role="menu"
        >
          {openSection.items.map((item) => {
            if (item.kind === 'leaf') {
              const Icon = item.icon;
              const active = matchesMenuHref(currentPath, item.href, search);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.panelItem} ${active ? styles.panelItemActive : ''}`}
                  onClick={() => handleNavigate(item.href)}
                  role="menuitem"
                >
                  <span className={styles.panelItemIcon}><Icon size={14} /></span>
                  <span>{item.label || (item.id === 'salesTeamView' ? 'Sales Team' : item.id === 'salesToday' ? 'Today' : item.id === 'salesJobs' ? 'Jobs' : item.label)}</span>
                </button>
              );
            }
            // NavParent, a collapsible group. Header is the click
            // target spanning the full row; clicking opens or closes
            // this group only (useMenuGroups). An empty group never
            // renders. Children mount inside a max-height transition
            // wrapper so expansion + collapse both animate at 200ms
            // ease-out.
            if (item.children.length === 0) return null;
            const Icon = item.icon;
            const groupKey = menuGroupKey(openSection.id, item.id);
            const holdsPage = groupHoldsPage(item.children, currentPath, search);
            const isParentOpen = groups.isOpen(groupKey, holdsPage);
            return (
              <div key={item.id} className={styles.panelGroup}>
                <button
                  type="button"
                  className={`${styles.panelGroupHeader} ${isParentOpen ? styles.panelGroupHeaderOpen : ''}`}
                  onClick={() => groups.toggle(groupKey, holdsPage)}
                  aria-expanded={isParentOpen}
                >
                  <span className={styles.panelItemIcon}><Icon size={12} /></span>
                  <span>{item.label || (item.id === 'salesTeamView' ? 'Sales Team' : item.id === 'salesToday' ? 'Today' : item.id === 'salesJobs' ? 'Jobs' : item.label)}</span>
                  <ChevronDown
                    size={12}
                    className={`${styles.panelGroupChev} ${isParentOpen ? styles.panelGroupChevOpen : ''}`}
                  />
                </button>
                <div
                  className={`${styles.panelGroupChildren} ${isParentOpen ? styles.panelGroupChildrenOpen : ''}`}
                  aria-hidden={!isParentOpen}
                >
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const active = matchesMenuHref(currentPath, child.href, search);
                    return (
                      <button
                        key={child.id}
                        type="button"
                        className={`${styles.panelItem} ${active ? styles.panelItemActive : ''}`}
                        onClick={() => handleNavigate(child.href)}
                        role="menuitem"
                        // Children of a collapsed parent are visually
                        // hidden via max-height:0 but still in the DOM
                        // — tabIndex -1 keeps them out of the focus
                        // order when the section's collapsed.
                        tabIndex={isParentOpen ? 0 : -1}
                      >
                        <span className={styles.panelItemIcon}><ChildIcon size={14} /></span>
                        <span>{child.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
